import { Row, Col, Button } from 'react-bootstrap';
import { BsPencilSquare } from 'react-icons/bs';
import { LinkContainer } from 'react-router-bootstrap';
import { useCurrencyFormatter } from '../../../hooks/use-currency-formatter';
import { Card } from '../../Card';
import { InfoIcon } from '../../InfoIcon';
import { TooltippedText } from '../../TooltippedText';
import { getRatesFields, RatesType } from './RateDisplay.utils';

const SENDIT_FEE_EXPLANATION = 'Rate does not include any approved travel costs';

interface RateDisplayProps {
  isProviderLoading: boolean;
  isCurrentProvider: boolean;
  rates: RatesType;
  originalRates?: RatesType;
  minHeight?: number;
  className?: string;
  showEditLink?: boolean;
  showRateComparison?: boolean;
}

export const RateDisplay = ({
  isProviderLoading,
  isCurrentProvider,
  rates,
  originalRates,
  minHeight = 230,
  className = 'p-3 mt-3 h-100',
  showEditLink = false,
  showRateComparison = false,
}: RateDisplayProps) => {
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  const hasProposedRates =
    originalRates &&
    Boolean(
      (rates?.dayRate && rates.dayRate !== originalRates?.dayRate) ||
        (rates?.nightRate && rates.nightRate !== originalRates?.nightRate) ||
        (rates?.callRate && rates.callRate !== originalRates?.callRate) ||
        (rates?._24HRate && rates._24HRate !== originalRates?._24HRate)
    );

  return (
    <Card.SubCard style={{ minHeight: minHeight }} className={className}>
      <div className="d-flex justify-content-between align-items-start mb-0">
        <h5 className="mb-0 d-flex align-items-center">
          {!isCurrentProvider ? 'All-In Hourly Rate' : 'Hourly Rate'}
          {!isCurrentProvider ? (
            <TooltippedText spanClassName="p-1" tooltipContent={SENDIT_FEE_EXPLANATION}>
              <small className="color-primary">
                <InfoIcon size={12} className="tooltip-icon mb-1" />
              </small>
            </TooltippedText>
          ) : null}
        </h5>
        {showEditLink && isCurrentProvider && (
          <LinkContainer to="/app/provider/edit/rates">
            <Button size="sm" variant="link">
              <BsPencilSquare className="mb-1" />
            </Button>
          </LinkContainer>
        )}
      </div>
      {hasProposedRates && showRateComparison && (
        <div className="small bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle rounded p-2 my-2">
          {isCurrentProvider
            ? 'Rates proposed are different from your current marketplace rates. Please review carefully.'
            : "Your organization proposed rates that are different from the provider's marketplace rates."}
        </div>
      )}
      <Row className="g-3">
        {getRatesFields(rates).map(({ label, rate }) => {
          const originalRate = originalRates
            ? getRatesFields(originalRates).find((f) => f.label === label)?.rate
            : undefined;

          const hasChange =
            showRateComparison && originalRate !== undefined && originalRate !== rate;

          if (isProviderLoading) {
            return (
              <Col xs={6} sm={6} key={label}>
                <Card.Info isLoading={true} label={label}>
                  -
                </Card.Info>
              </Col>
            );
          }

          if (!rate) {
            return null;
          }

          return (
            <Col xs={6} sm={6} key={label}>
              <Card.Info label={label}>
                {hasChange ? (
                  <div>
                    <span className="text-decoration-line-through text-muted">
                      {formatCurrencyPostfix(originalRate)}
                    </span>
                    <span
                      className={`ms-2 ${rate > originalRate ? 'text-success' : 'text-danger'}`}
                    >
                      {formatCurrencyPostfix(rate)}
                    </span>
                  </div>
                ) : (
                  formatCurrencyPostfix(rate)
                )}
              </Card.Info>
            </Col>
          );
        })}
      </Row>
    </Card.SubCard>
  );
};
